import { useMemo } from 'react';

import useAuth from './useAuth';

const MIN_AGE = 18;

/**
 * Provincias habilitadas, normalizadas (sin tildes, en minusculas).
 * Se incluyen variantes conocidas de CABA porque el backend no siempre
 * devuelve el mismo texto ("CABA", "Capital Federal", etc.).
 */
const ELIGIBLE_PROVINCES = new Set([
  'caba',
  'capital federal',
  'ciudad autonoma de buenos aires',
  'ciudad de buenos aires',
  'buenos aires',
  'salta',
  'mendoza',
  'santa fe',
  'la rioja',
  'neuquen',
  'chubut',
]);

const normalize = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/**
 * Chequeo puro de la segmentación de BetWarrior: mayor o igual a 18 años y
 * domicilio declarado en una de las provincias habilitadas. Se exporta por
 * separado del hook para poder evaluarse con datos que todavía no están en
 * el atom (p.ej. la provincia recién elegida en el form de datos
 * personales, antes de que useAuth() se actualice).
 */
export const isBetWarriorEligible = ({
  age,
  pcia,
}: {
  age?: number;
  pcia?: string;
}) => {
  const isAdult = (age ?? 0) >= MIN_AGE;
  const isInEligibleProvince = Boolean(
    pcia && ELIGIBLE_PROVINCES.has(normalize(pcia))
  );

  return isAdult && isInEligibleProvince;
};

/**
 * Segmentación para mostrar contenido de BetWarrior.
 * Reutiliza los datos ya cargados por useAuth (userInitialData), sin
 * disparar ningún fetch adicional.
 */
const useBetWarriorSegmentation = () => {
  const { userInitialData } = useAuth();

  const isEligible = useMemo(() => {
    if (!userInitialData) {
      return false;
    }

    return isBetWarriorEligible({
      age: userInitialData.edad_dt,
      pcia: userInitialData.provincia,
    });
  }, [userInitialData]);

  return { isEligible };
};

export default useBetWarriorSegmentation;
